import {
    Alert24Regular,
    Bot24Filled,
    CalendarClock24Regular,
    Checkmark24Filled,
    DocumentBulletList24Regular,
    Flash24Regular,
    Mail24Regular,
    Megaphone24Regular,
    Person24Regular,
    Settings24Regular
} from "@fluentui/react-icons";
import {FC, useMemo} from "react";
import {
    ConversationMessage,
    DiagnosisReport,
    MessageAction,
    MessageActionKind
} from "./intelligenceModels";
import IntelligenceActivity from "./IntelligenceActivity";
import IntelligenceDiagnosisCard from "./IntelligenceDiagnosisCard";
import IntelligenceMorningBrief from "./IntelligenceMorningBrief";
import IntelligenceMarkdown from "./IntelligenceMarkdown";
import {getAgentIcon} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";
import {DataTable} from "@/components/DataTable";
import EnhancedChartComponent from "@/coach-ai/src/components/Chart/EnhancedChart";
import {
    ICustomColumnsTransformation,
    IDataTableData
} from "@/features/application/api/applicationModels";

const EMPTY_CUSTOM_COLUMNS: ICustomColumnsTransformation = {};

const ACTION_ICONS: Record<MessageActionKind, FC> = {
    campaign: Megaphone24Regular,
    automation: Flash24Regular,
    settings: Settings24Regular,
    report: DocumentBulletList24Regular,
    alert: Alert24Regular,
    schedule: CalendarClock24Regular,
    comms: Mail24Regular
};

interface IntelligenceMessageProps {
    message: ConversationMessage;
    agent?: AgentIdentity | null;
    onFollowUp: (prompt: string) => void;
    onRunAction: (action: MessageAction) => void;
    completedActionIds: string[];
    onLaunchDiagnosisWorkflow: (report: DiagnosisReport) => void;
    /** Whether the drawer is currently maximized — data tables/charts only render when expanded. */
    expanded: boolean;
}

const IntelligenceMessage: FC<IntelligenceMessageProps> = ({
    message,
    agent,
    onFollowUp,
    onRunAction,
    completedActionIds,
    onLaunchDiagnosisWorkflow,
    expanded
}) => {
    const isUser = message.role === "user";

    const tableData = useMemo<IDataTableData[]>(() => {
        if (!message.dataPoints) {
            return [];
        }
        return Object.values(message.dataPoints) as IDataTableData[];
    }, [message.dataPoints]);

    const hasValidTableData =
        tableData.length > 0 && (message.columnsMetadata?.length ?? 0) > 0;
    const showData = !isUser && expanded && message.isComplete && hasValidTableData;
    const showAgent = !isUser && Boolean(agent);
    const AgentIcon = agent ? getAgentIcon(agent) : undefined;

    return (
        <div
            className={`pi-msg pi-msg--${isUser ? "user" : "assistant"}`}
            style={
                showAgent && agent
                    ? ({"--par-agent": agent.accent} as React.CSSProperties)
                    : undefined
            }
        >
            <span
                className={`pi-msg__avatar${
                    showAgent ? " pi-msg__avatar--agent" : ""
                }`}
            >
                {isUser ? (
                    <Person24Regular />
                ) : showAgent && AgentIcon ? (
                    <AgentIcon />
                ) : (
                    <Bot24Filled />
                )}
            </span>
            <div className="pi-msg__col">
                {!isUser && message.activity && message.activity.length > 0 && (
                    <IntelligenceActivity
                        steps={message.activity}
                        isComplete={message.isComplete ?? false}
                    />
                )}
                {(isUser || message.text) && (
                    <>
                        {showAgent && agent && (
                            <span className="pi-msg__author">{agent.name}</span>
                        )}
                        <div className="pi-msg__bubble">
                            {isUser ? (
                                message.text
                            ) : (
                                <IntelligenceMarkdown text={message.text} />
                            )}
                        </div>
                    </>
                )}

                {!isUser && message.diagnosisReport && (
                    <IntelligenceDiagnosisCard
                        report={message.diagnosisReport}
                        onLaunchWorkflow={onLaunchDiagnosisWorkflow}
                    />
                )}

                {!isUser && message.morningBrief && (
                    <IntelligenceMorningBrief brief={message.morningBrief} />
                )}

                {!isUser && hasValidTableData && message.isComplete && !expanded && (
                    <div className="pi-data-hint">
                        Data table{message.highchart ? " and chart" : ""} available —
                        expand the drawer to view.
                    </div>
                )}

                {showData && message.highchart && (
                    <div className="pi-msg__chart">
                        <EnhancedChartComponent
                            highchartConfig={message.highchart}
                            data={tableData}
                            columnsMetadata={message.columnsMetadata ?? []}
                        />
                    </div>
                )}

                {showData && (
                    <div className="pi-msg__table">
                        <DataTable
                            data={tableData}
                            staticColumns={message.columnsMetadata ?? []}
                            customColumns={EMPTY_CUSTOM_COLUMNS}
                            sortColumnKey={message.sortColumn ?? ""}
                            sortDirection={message.sortDirection}
                            itemsPerPage={10}
                            hideNoResultsMessage={true}
                            screenWidth={window.innerWidth}
                            retainActivePage={true}
                            exportFileName={message.fileName || "coach-ai-data"}
                        />
                    </div>
                )}

                {message.metrics && message.metrics.length > 0 && (
                    <div className="pi-metrics">
                        {message.metrics.map((metric) => (
                            <div className="pi-metric" key={metric.label}>
                                <span
                                    className={`pi-metric__value pi-metric__value--${metric.direction}`}
                                >
                                    {metric.value}
                                </span>
                                <span className="pi-metric__label">
                                    {metric.label}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {message.sources && message.sources.length > 0 && (
                    <div className="pi-sources">
                        {message.sources.map((source) => (
                            <span className="pi-source" key={source}>
                                {source}
                            </span>
                        ))}
                    </div>
                )}

                {/* Suggested actions are only relevant for the BRIEFING investigation
                    path — ANOMALY diagnoses already surface their own "Turn into an
                    action plan" CTA, and plain answers have no action context. */}
                {!isUser &&
                    message.morningBrief &&
                    message.actions &&
                    message.actions.length > 0 && (
                        <div className="pi-actions">
                            {message.actions.map((action) => {
                                const Glyph = ACTION_ICONS[action.kind];
                                const isDone = completedActionIds.includes(
                                    action.id
                                );
                                return (
                                    <div className="pi-action" key={action.id}>
                                        <span className="pi-action__icon">
                                            <Glyph />
                                        </span>
                                        <div className="pi-action__body">
                                            <span className="pi-action__category">
                                                {action.category}
                                            </span>
                                            <span className="pi-action__title">
                                                {action.title}
                                            </span>
                                            <span className="pi-action__desc">
                                                {action.description}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={`pi-action__btn${
                                                isDone ? " pi-action__btn--done" : ""
                                            }`}
                                            disabled={isDone}
                                            onClick={() => onRunAction(action)}
                                        >
                                            {isDone ? (
                                                <>
                                                    <Checkmark24Filled />
                                                    Done
                                                </>
                                            ) : (
                                                <>
                                                    {action.ctaLabel}
                                                    <span className="pi-action__btn-arrow">
                                                        →
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                {message.followUps && message.followUps.length > 0 && (
                    <div className="pi-followups">
                        {message.followUps.map((followUp) => (
                            <button
                                type="button"
                                className="pi-chip"
                                key={followUp}
                                onClick={() => onFollowUp(followUp)}
                            >
                                {followUp}
                            </button>
                        ))}
                    </div>
                )}

                <span className="pi-msg__time">{message.timestamp}</span>
            </div>
        </div>
    );
};

export default IntelligenceMessage;
